import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Forms, SaveButton, SystemText, Loading, DeleteWarning } from 'e4shared';
import TaskSummary from '../taskSummary';
import TaskShareCommentSection from '../taskShareCommentSection';
import TaskCommentSection from '../taskCommentSection';
import TaskFeedbackSection from '../taskFeedbackSection';
import TaskPathwaySection from '../taskPathwaySection';
import TaskAppraisalSection from '../taskAppraisalSection';
import { translateCourse } from 'helpers/translations';
import { lmsNightlySystems, lmsSystems } from 'helpers/lms';
import Assets from 'components/general/assets';
import { uploadFileToAzure, uploadType } from 'helpers/azure';
import { getPermissions } from 'helpers/permissions';
import { apiGet, apiPatch, apiDelete } from 'services';
import TaskRejectionsSection from '../taskRejectionsSection';
import ShareModal from '../shareModal';
import { loadUserStats } from 'actions/user';

export const TaskContentSection = ({ task, setTask, profile }) => {
    const [competencies, setCompetencies] = React.useState([]);
    const [comments, setComments] = React.useState('');
    const [assets, setAssets] = React.useState([]);
    const [video, setVideo] = React.useState(null);
    const [showFeedbackError, setShowFeedbackError] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [showRejections, setShowRejections] = React.useState('');
    const [showShare, setShowShare] = React.useState('');
    const [showReturnWarning, setShowReturnWarning] = React.useState(false);
    const [commentsError, setCommentsError] = React.useState('');
    const [assetError, setAssetError] = React.useState('');
    const [error, setError] = React.useState(false);
    const [showFeedBackSection, setShowFeedBackSection] = React.useState(false);
    const [rating, setRating] = React.useState(0);
    const [showShareSuccess, setShowShareSuccess] = React.useState(false);

    const [goalMet, setGoalMet] = React.useState(false);
    const [feedback_1, setFeedback_1] = React.useState('');
    const [feedback_2, setFeedback_2] = React.useState('');
    const [feedback_3, setFeedback_3] = React.useState('');

    const { user, coaching, downline } = useSelector(state => state.user);
    const [permissions, setPermissions] = React.useState(getPermissions(profile, user, coaching, downline));

    const [loading, setLoading] = React.useState(task === undefined);

    React.useEffect(() => {
        if (task) {
            setComments(task.comments);
            setAssets(task.completed_Assets);
            setVideo(task.completed_Video_Asset);
            setGoalMet(task.goal_Met);
            setFeedback_1(task.feedback_1);
            setFeedback_2(task.feedback_2);
            setFeedback_3(task.feedback_3);

            if (task.competencies?.length > 0) {
                setCompetencies(task.competencies);
            } else {
                setRating(task.rating);
            }
        }
    }, [task]);

    React.useEffect(() => {
        if (user.administrator) {
            setShowFeedBackSection(true);
        } else if (task?.profile_Id === user.id) {
            setShowFeedBackSection(true);
        } else if (task?.publicAccessOnly) {
            setShowFeedBackSection(false);
        } else {
            setShowFeedBackSection(true);
        }
    }, [user, task]);

    React.useEffect(() => {
        if (profile?.id && user?.id) {
            setPermissions(getPermissions(profile, user, coaching, downline));
        }
    }, [profile?.id, user?.id]);

    const text = {
        attempts: {
            title: SystemText('Objective/Details/Attempts'),
            single: SystemText('Objective/Details/Attempts/Single'),
            plural: x => SystemText('Objective/Details/Attempts/Plural', x),
        },
        previous: {
            single: SystemText('Objective/Details/ShowPrevious/Single'),
            plural: SystemText('Objective/Details/ShowPrevious/Plural'),
        },
        share: SystemText('Objective/Share'),
        comments: SystemText('Objective/Comments'),
        buttons: {
            edit: SystemText('Objective/Buttons/Edit'),
            share: SystemText('Objective/Buttons/Share'),
        },
        nightly: SystemText('Objective/Resources/Nightly'),
        instructions: SystemText('Objective/Summary/Instructions'),
        summary: SystemText('Objective/Summary/Summary'),
        scenario: SystemText('Objective/Summary/Scenario'),
    };

    const dispatchSetSaving = value => dispatch(setSaving(value));

    const api = {
        shareTask: (sharedTask, callback) =>
            apiPatch(
                `share-task`,
                sharedTask,
                user.guid,
                () => {
                    setShowShareSuccess(true);
                    callback?.();
                    api.loadTask(task.id, false);
                },
                () => setError(true)
            ),
        saveComments: comment =>
            apiPatch(
                `profile/-1/task/comment`,
                comment,
                user.guid,
                () => {
                    setShowSuccess(true);
                    setSaving(false);
                    api.loadTask(task.id, false);
                    if (comment.completed) {
                        dispatch(loadUserStats());
                        posthog.capture('objective-completed', {
                            video: task.video,
                            taskType: task.taskType,
                            ai: task.challenge_Id?.length ? true : false,
                        });
                    }
                },
                () => setError(true)
            ),
        saveSharedComments: (comment, callback) =>
            apiPatch(
                `profile/-1/task/share-comment`,
                comment,
                user.guid,
                () => {
                    callback?.();
                    api.loadTask(task.id, false);
                    comment.completed && dispatch(loadUserStats());
                },
                () => setError(true)
            ),
        deleteSharedComment: deleteId =>
            apiDelete(
                `task/share-comment/${deleteId}`,
                null,
                user.guid,
                () => {
                    api.loadTask(task.id, false);
                },
                () => setError(true)
            ),
        saveFeedback: feedback =>
            apiPatch(
                `profile/-1/task/feedback`,
                feedback,
                user.guid,
                () => {
                    setShowSuccess(true);
                    setSaving(false);
                    api.loadTask(feedback.profile_Task_Id, false);
                    (feedback.approved || feedback.rejected) && dispatch(loadUserStats());
                },
                () => setError(true)
            ),
        loadTask: (profileTaskId, refresh) => {
            setLoading(true);
            apiGet(
                `profile/-1/task/${profileTaskId}${refresh ? '?refresh=true' : ''}`,
                user.guid,
                response => {
                    if (response.completed_Video_Asset?.insights_Id) {
                        response.insights_Id = response.completed_Video_Asset.insights_Id;
                        response.insights = response.completed_Video_Asset.insights
                            ? JSON.parse(response.completed_Video_Asset.insights)
                            : null;
                        response.video = response.insights?.videos ? response.insights.videos[0] : { insights: {} };
                    }
                    setTask(response);
                    setLoading(false);
                },
                () => setError(true)
            );
        },
    };

    //const showComments = !task.pathway_Assignment_Id && !task.appraisal_Id;
    const shareComments = task.share_Comments ? task.share_Comments.filter(c => c.rejection_Id === null) : [];

    const canPoke = permissions.objectives.canPoke;
    const canViewPathways = permissions.pathways.canView;
    const rejectedLength = task.rejections ? task.rejections.length : 0;
    //const isCoach = task.coach_Id === user.id;
    const isUser = task.profile_Id === user.id;
    const enabled = task.date_Completed || rejectedLength > 0;

    const uplineManager = task.manager_Id !== user.id && downline && downline.some(d => d.id === task.profile_Id);
    const isManager = task.manager_Id === user.id;

    const noRating =
        task.competency_Ids?.length && task.competencies?.length > 0
            ? competencies.filter(c => c.rating > 0).length < task.competency_Ids.length
            : task.rating === 0 && rating === 0;
    const submitDisabled = task.requires_Feedback && (noRating || !feedback_1 || !feedback_2 || !feedback_3);
    const setPageComments = comment => {
        if (comment) setCommentsError('');
        setComments(comment);
    };

    const setPageVideo = value => {
        if (value) setAssetError('');
        setVideo(value);
    };

    const setPageAssets = value => {
        if (value) setAssetError('');
        setAssets(value);
    };
    const setCompetencyRating = (rating, competency) => {
        const comps = [...competencies];
        if (competency) {
            const comp = comps.find(c => c.id === competency);
            if (comp) {
                comp.rating = rating;
            } else {
                comps.push({ id: competency, rating });
            }

            //const taskComps = [...task.competencies];
            //taskComps.find(c => c.id === competency).rating = rating;
            //setTask({ ...task, competencies: taskComps });

            setCompetencies(comps);
        } else {
            setRating(rating);
        }
    };

    const saveShares = (private_Share_Ids, public_Share_Ids, callback) => {
        const sharedTask = {
            id: task.id,
            private_Share_Ids,
            public_Share_Ids,
        };

        api.shareTask(sharedTask, callback);
    };

    const saveFeedback = (approved, rejected) => {
        const feedback = {
            profile_Task_Id: task.id,
            goal_Met: goalMet,
            feedback_1: feedback_1,
            feedback_2: feedback_2,
            feedback_3: feedback_3,
            rating: rating,
            competencies: competencies,
            approved,
            rejected,
        };

        api.saveFeedback(feedback);
    };

    const canFeedback =
        (user.administrator || isManager || uplineManager) &&
        task.requires_Manager &&
        task.date_Completed &&
        !task.approved &&
        !task.pathway_Assignment_Id &&
        !task.appraisal_Id;

    let mandatoryAssets = [];
    let extraMessage = '';

    const saveComments = async completed => {
        const comment = {
            id: -1,
            profile_Task_Id: task.id,
            comments: comments,
            enrollment_Id: task.enrollment_Id,
            completed_Assets: assets,
            completed_Video_Asset: video,
            completed,
        };

        const uploadFile =
            comment.completed_Video_Asset && !comment.completed_Video_Asset.source ? comment.completed_Video_Asset : null;

        if (uploadFile && typeof uploadFile === 'object' && uploadFile.name) {
            const newFile = {
                ...uploadFile,
                name: uploadFile.name,
                size: uploadFile.size,
                companyId: uploadFile.companyId,
                url: uploadFile.url,
                folder: uploadType.taskCompletion,
                source: 'file',
            };

            comment.task_Completion = await uploadFileToAzure(
                user.guid,
                uploadFile,
                uploadType.taskCompletion,
                progress => setProgress(progress),
                response => {
                    comment.completed_Video_Asset = { ...newFile, path: response.path, folder: response.folder };
                    api.saveComments(comment);
                }
            );
        } else {
            api.saveComments(comment);
        }
    };

    const saveDraft = () => {
        saveComments(false);
    };

    const validate = () => {
        const tempCommentsError = comments ? '' : SystemText('Objective/Pitchback/Errors/Comments');
        const tempAssetError =
            !task.video || video || task.completed_Video_Asset_Id > 0 ? '' : SystemText('Objective/Pitchback/Errors/Video');
        if (tempCommentsError || tempAssetError) {
            setCommentsError(tempCommentsError);
            setAssetError(tempAssetError);
            setSaving(false);
        } else {
            saveComments(true);
        }
    };

    lmsSystems.forEach(lms => {
        if (task.lms_Task || (task.lms?.some(x => x === lms) && task.assets)) {
            const lmsAssests = (task.assets || []).filter(a => a.source === lms);
            mandatoryAssets = mandatoryAssets.concat(lmsAssests);

            if (lmsNightlySystems.some(x => x === lms)) {
                extraMessage = lmsAssests.length > 0 ? text.nightly : '';
            }
        }
    });

    const returnFeedback = confirm => {
        setShowReturnWarning(false);

        confirm && saveFeedback(false, true);
        !confirm && dispatchSetSaving(false);
    };

    const buttons = () => {
        const userButtons = !task.completed && isUser;
        const managerButtons = !task.fully_Completed && task.date_Completed && permissions.objectives.canApproveAndReject;

        if (!userButtons && !managerButtons) return null;

        const disabled = task.lms_Task && mandatoryAssets.some(a => !a.completion?.completed_At);

        return (
            <div className="table-btns">
                <div className="btn-row mt10 mb10">
                    {userButtons && (
                        <>
                            <SaveButton
                                data-testid="button-draft"
                                saving={saving}
                                setSaving={setSaving}
                                className="btn btn-primary mr10"
                                onClick={() => saveDraft()}
                            >
                                {SystemText('Objective/Pitchback/Buttons/Draft')}
                            </SaveButton>
                            <SaveButton
                                disabled={disabled}
                                data-testid="button-save"
                                saving={saving}
                                setSaving={setSaving}
                                className="btn btn-success"
                                onClick={() => validate()}
                            >
                                {SystemText('Objective/Pitchback/Buttons/Save')}
                            </SaveButton>
                        </>
                    )}
                    {managerButtons && (
                        <>
                            <SaveButton
                                saving={saving}
                                setSaving={setSaving}
                                className="btn btn-danger mr10"
                                onClick={
                                    submitDisabled
                                        ? () => {
                                              setShowFeedbackError(true);
                                              setSaving(false);
                                          }
                                        : () => {
                                              setShowFeedbackError(false);
                                              setShowReturnWarning(true);
                                          }
                                }
                            >
                                {SystemText('Objective/Feedback/Buttons/Return')}
                            </SaveButton>
                            <SaveButton
                                saving={saving}
                                setSaving={setSaving}
                                className="btn btn-primary mr10"
                                onClick={() => {
                                    saveFeedback(false);
                                    setShowFeedbackError(false);
                                }}
                            >
                                {SystemText('Objective/Feedback/Buttons/Draft')}
                            </SaveButton>
                            <SaveButton
                                saving={saving}
                                setSaving={setSaving}
                                className="btn btn-success"
                                onClick={
                                    submitDisabled
                                        ? () => {
                                              setShowFeedbackError(true);
                                              setSaving(false);
                                          }
                                        : () => {
                                              setShowFeedbackError(false);
                                              saveFeedback(true);
                                          }
                                }
                            >
                                {task.metric_Task && !goalMet
                                    ? SystemText('Objective/Feedback/Buttons/Close')
                                    : SystemText('Objective/Feedback/Buttons/Approve')}
                            </SaveButton>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const actionSection = () => {
        return (
            <>
                {task.pathway_Assignment_Id && !task.pathway_Id ? (
                    <TaskPathwaySection task={task} />
                ) : task.appraisal_Id ? (
                    <TaskAppraisalSection task={task} />
                ) : (
                    <>
                        <TaskCommentSection
                            task={task}
                            profile={profile}
                            user={user}
                            clearShowSuccess={() => {
                                setProgress(0);
                                setShowSuccess(false);
                            }}
                            extraMessage={extraMessage}
                            showSuccess={showSuccess}
                            comments={comments}
                            setComments={setComments}
                            assets={assets}
                            setAssets={setAssets}
                            video={video}
                            setVideo={setVideo}
                            canFeedback={canFeedback}
                            saveComments={saveComments}
                            progress={progress}
                            mandatoryAssets={mandatoryAssets.map(x => {
                                return translateCourse(user.language_Code, x);
                            })}
                            setPageComments={setPageComments}
                            setPageVideo={setPageVideo}
                            setPageAssets={setPageAssets}
                            commentsError={commentsError}
                            assetError={assetError}
                        />

                        {showFeedBackSection && (
                            <TaskFeedbackSection
                                task={task}
                                profile={profile}
                                user={user}
                                downline={downline}
                                clearShowSuccess={() => setShowSuccess(false)}
                                showSuccess={showSuccess}
                                goalMet={goalMet}
                                feedback_1={feedback_1}
                                feedback_2={feedback_2}
                                feedback_3={feedback_3}
                                rating={rating}
                                competencies={competencies}
                                setGoalMet={setGoalMet}
                                setFeedback1={setFeedback_1}
                                setFeedback2={setFeedback_2}
                                setFeedback3={setFeedback_3}
                                setRating={setCompetencyRating}
                                saveFeedback={saveFeedback}
                                canSubmitFeedback={permissions.objectives.canApproveAndReject}
                                showFeedbackError={showFeedbackError}
                                submitDisabled={submitDisabled}
                            />
                        )}
                    </>
                )}
            </>
        );
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <section className="content mtm10" data-testid="task-content">
                <div className="mb10">
                    <TaskSummary task={task} canPoke={canPoke} canViewPathways={canViewPathways} />
                </div>
                <div className="row">
                    <div className="col-sm-8">
                        <Box
                            title={task.name}
                            fa={task.icons?.map(x => `fa-${x}`).find(x => x)}
                            footer={buttons()}
                            className="box-objective"
                            hideEllipsis
                        >
                            {task.scenario && <Forms.Label label={text.scenario} value={task.scenario} />}
                            {task.description && <Forms.Label label={text.summary} value={task.description} />}
                            {task.instructions && <Forms.Label label={text.instructions} value={task.instructions} />}
                            {!task.lms_Task && task.assets && (
                                <div className={task.instructions ? 'mt10 mb10' : ''}>
                                    <Assets list assets={task.assets} />
                                </div>
                            )}
                            <div className="">{actionSection()}</div>
                        </Box>
                    </div>
                    <div className="col-sm-4">
                        {rejectedLength > 0 && (
                            <Box className="box-danger" title={text.attempts.title}>
                                <div className="mb10">
                                    <label>
                                        {rejectedLength === 1 ? text.attempts.single : text.attempts.plural(rejectedLength)}
                                    </label>
                                    <div>
                                        <button className="btn btn-warning" onClick={() => setShowRejections(true)}>
                                            {rejectedLength === 1 ? text.previous.single : text.previous.plural}
                                        </button>
                                    </div>
                                </div>
                            </Box>
                        )}
                        {!task.publicAccessOnly && permissions.objectives.canShare?.(task.owner_Id) && enabled && (
                            <Box title={text.share.title}>
                                <div className="mb10">
                                    <label>{text.share}</label>
                                    <div>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                setShowShareSuccess(false);
                                                setShowShare(true);
                                            }}
                                        >
                                            <i className="fa fa-share-alt mr5" />
                                            {text.buttons.share}
                                        </button>
                                    </div>
                                </div>
                            </Box>
                        )}

                        <Box
                            className=""
                            title={text.comments}
                            postButton={() => <span className="box-counter">{shareComments.length}</span>}
                        >
                            <TaskShareCommentSection
                                permissions={permissions}
                                saveSharedComments={api.saveSharedComments}
                                deleteSharedComment={api.deleteSharedComment}
                                clearShowSuccess={() => setShowSuccess(false)}
                                task={task}
                                shareComments={shareComments}
                                profile={profile}
                                user={user}
                                downline={downline}
                            />
                        </Box>
                    </div>
                </div>
            </section>

            <DeleteWarning
                message={SystemText('Objective/Feedback/Warning/Return')}
                show={showReturnWarning}
                close={confirm => returnFeedback(confirm)}
            />

            <TaskRejectionsSection
                show={showRejections}
                close={() => setShowRejections(false)}
                task={task}
                profile={profile}
            />

            <ShareModal
                show={showShare}
                showSuccess={showShareSuccess}
                close={() => setShowShare(false)}
                onShare={saveShares}
                task={task}
                profile={profile}
            />
        </>
    );
};

export default TaskContentSection;
